"use client";

import type React from "react";

import { motion, AnimatePresence } from "framer-motion";
import {
  Camera,
  Palette,
  Sparkles,
  Wand2,
  Type,
  Settings,
  ChevronDown,
  RefreshCw,
  Send,
  VideoIcon,
  Heart,
  Download,
  Zap,
  History,
  X,
  Share,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import eventBus from "../../utils/eventBus";
import {
  GoogleGenAI,
  PersonGeneration,
  SafetyFilterLevel,
} from "@google/genai";
import uploadImageToFirebase from "../../utils/uploadImageToFirebase";
import PublishContent from "./PublishContent";
import { useDispatch, useSelector } from "react-redux";
import {
  addLibraryItem,
  selectLibraryContent,
  selectUserGlobalData,
} from "../../store/dreamworldSlice";
import { useNavigate } from "react-router-dom";
import dreamWorldProvider from "../../DreamWorldProvider";
import { LibraryItem, InteractiveEditingData } from "../../types/library";
import { AppDispatch } from "../../store/store";

import { v4 as uuidv4 } from "uuid";
import { Timestamp } from "firebase/firestore";
import LoadingIndicator from "../ui/LoadingIndicator";
import { hasEnoughFlux, decreaseFluxBalance } from "../../utils/fluxManager";
import InsufficientFluxModal from "../modals/InsufficientFluxModal";

interface ImageGeneratorProps {
  prompt?: string;
}

const ImageGenerator: React.FC<ImageGeneratorProps> = ({
  prompt: initialPrompt,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const userGlobalData = useSelector(selectUserGlobalData);
  const libraryContent = useSelector(selectLibraryContent);
  // Existing State
  const [prompt, setPrompt] = useState(initialPrompt || "");
  const [isFocused, setIsFocused] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState("Photorealistic");
  const [stylesOpen, setStylesOpen] = useState(true);
  const [quickIdeasOpen, setQuickIdeasOpen] = useState(true);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [quality, setQuality] = useState("standard");
  const [seed, setSeed] = useState("");
  const [numberOfImages, setNumberOfImages] = useState(4);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // New State for generation, results, and history
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<LibraryItem[]>([]);

  const [history, setHistory] = useState<LibraryItem[]>([]);
  const [currentHistoryId, setCurrentHistoryId] = useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [selectedImageForPublish, setSelectedImageForPublish] =
    useState<LibraryItem | null>(null);

  const resultsRef = useRef<HTMLDivElement>(null);

  const [hasSufficientFlux, setHasSufficientFlux] = useState(true);
  const [isFluxModalOpen, setIsFluxModalOpen] = useState(false);

  useEffect(() => {
    if (userGlobalData) {
      const fluxCost = numberOfImages * 5;
      hasEnoughFlux(userGlobalData.user.uid, fluxCost).then(
        setHasSufficientFlux
      );
    }
  }, [userGlobalData, numberOfImages]);

  useEffect(() => {
    const savedHistory = localStorage.getItem("imageGeneratorHistory2");
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setHistory(parsedHistory);
      } catch (error) {
        console.error("Error loading history:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (history.length > 0) {
      const limitedHistory = history.slice(0, 100);
      localStorage.setItem(
        "imageGeneratorHistory2",
        JSON.stringify(limitedHistory)
      );
    }
  }, [history]);

  useEffect(() => {
    const handleHistoryUpdate = () => {
      try {
        const raw = localStorage.getItem("imageGeneratorHistory2");
        if (raw) {
          setHistory(JSON.parse(raw));
        }
      } catch (err) {
        console.error("Failed to load history:", err);
      }
    };

    // listen to event
    eventBus.on("imageGeneratorHistory2:updated", handleHistoryUpdate);

    // run once on mount so it's in sync
    handleHistoryUpdate();

    // cleanup
    return () => {
      eventBus.off("imageGeneratorHistory2:updated", handleHistoryUpdate);
    };
  }, []);

  useEffect(() => {
    if (generatedImages.length > 0 && window.innerWidth <= 768) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 500);
    }
  }, [generatedImages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        120
      )}px`;
    }
  }, [prompt]);
  const safetySettings = [
    {
      category: "HARM_CATEGORY_HARASSMENT",
      threshold: "BLOCK_LOW_AND_ABOVE",
    },
    {
      category: "HARM_CATEGORY_HATE_SPEECH",
      threshold: "BLOCK_LOW_AND_ABOVE",
    },
  ];

  const handleGenerate = async () => {
    if (!prompt || !userGlobalData) return;

    const fluxCost = numberOfImages * 5;
    if (!hasSufficientFlux) {
      setIsFluxModalOpen(true);
      return;
    }

    setIsGenerating(true);
    setGeneratedImages([]);

    const success = await decreaseFluxBalance(
      userGlobalData.user.uid,
      fluxCost
    );
    if (!success) {
      setIsGenerating(false);
      setHasSufficientFlux(false);
      setIsFluxModalOpen(true);
      return;
    }

    try {
      const ai = new GoogleGenAI({
        apiKey: "AIzaSyDKqbfR_vItoj4GvwzBv8lBfyJajcEuqaA",
      });
      const response = await ai.models.generateImages({
        model: "imagen-4.0-generate-001",
        prompt: `${selectedStyle} style: ${prompt}`,
        config: {
          numberOfImages: numberOfImages,
          aspectRatio: aspectRatio as any,
          personGeneration: PersonGeneration.ALLOW_ALL,
          includeSafetyAttributes: false,
          // enhancePrompt: true,
          // safetyFilterLevel: SafetyFilterLevel.BLOCK_NONE,
          outputMimeType: "image/jpeg",
          includeRaiReason: true,
        },
      });

      if (response?.generatedImages) {
        const newImages: LibraryItem[] = [];
        for (const generatedImage of response.generatedImages) {
          if (generatedImage.image?.imageBytes) {
            const base64String = generatedImage.image.imageBytes;
            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "image/jpeg" });
            const file = new File([blob], "generated_image.jpg", {
              type: "image/jpeg",
            });
            const downloadUrl = await uploadImageToFirebase(
              file,
              "postsImages"
            );
            const docId = uuidv4();
            const newLibraryItem: LibraryItem = {
              id: docId,
              ownerId: userGlobalData.user.uid,
              type: "Image",
              fullUrl: downloadUrl,
              thumbnailUrl: downloadUrl,
              prompt: prompt,
              isFavorite: false,
              views: 0,
              likes: 0,
              tags: [selectedStyle.toLowerCase()],
              size: `${(blob.size / 1024 / 1024).toFixed(2)} MB`,
              createdAt: Timestamp.now(),
              isPublished: false,
              title: prompt,
              creator: userGlobalData.userData!,
            };

            await dreamWorldProvider.saveLibraryItem(newLibraryItem);
            dispatch(addLibraryItem(newLibraryItem));

            newImages.push(newLibraryItem);
          }
        }
        setGeneratedImages(newImages);
        setHistory((prev) => [...newImages, ...prev]);
      }
    } catch (error) {
      console.error("Error generating images:", error);
    }

    setIsGenerating(false);
  };

  useEffect(() => {
    if (prompt.trim()) {
      eventBus.emit("fab:config", {
        icon: "generate",
        text: "Generate",
        action: handleGenerate,
      });
    } else {
      eventBus.emit("fab:reset");
    }
  }, [prompt, userGlobalData, hasSufficientFlux]);

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  };

  const loadHistoryItem = (historyItem: LibraryItem) => {
    setPrompt(historyItem.prompt as string);
    setSelectedStyle(historyItem.tags[0]);
    setGeneratedImages([historyItem]);
    setCurrentHistoryId(historyItem.id);
    setIsHistoryOpen(false);
  };

  const handlePublish = (image: LibraryItem) => {
    setSelectedImageForPublish(image);
    setPublishModalOpen(true);
  };

  const navigate = useNavigate();

  const handleContinueEditing = async (image: LibraryItem) => {
    if (!userGlobalData) return;

    const docId = uuidv4();
    const newEditingData: InteractiveEditingData = {
      id: docId,
      ownerId: userGlobalData.user.uid,
      title: image.title || "Untitled Editing Session",
      originalImageUrls: [image.fullUrl],
      createdAt: Timestamp.now(),
      turnsCount: 1,
      editings: [
        {
          id: uuidv4(),
          imageUrl: [image.fullUrl],
          request: image.prompt || "Continue editing",
          generatedImageUrl: image.fullUrl,
          date: Timestamp.now(),
          isSavedToLib: true,
          isPublished: image.isPublished || false,
        },
      ],
    };

    await dreamWorldProvider.addInteractiveEditingData(newEditingData);
    navigate(`/interactive-editing/${docId}`);
  };

  const styles = [
    {
      name: "Photo",
      value: "Photorealistic",
      icon: Camera,
      gradient: "from-emerald-500 to-teal-600",
    },
    {
      name: "Digital",
      value: "Digital art",
      icon: Palette,
      gradient: "from-purple-500 to-pink-600",
    },
    {
      name: "Cinematic",
      value: "Cinematic",
      icon: VideoIcon,
      gradient: "from-orange-500 to-red-600",
    },
    {
      name: "Anime",
      value: "Anime",
      icon: Sparkles,
      gradient: "from-blue-500 to-cyan-600",
    },
    {
      name: "Abstract",
      value: "Abstract",
      icon: Wand2,
      gradient: "from-violet-500 to-purple-600",
    },
    {
      name: "Minimal",
      value: "Minimalist",
      icon: Type,
      gradient: "from-gray-500 to-slate-600",
    },
  ];

  const examplePrompts = [
    "Serene mountain lake at dawn",
    "Cyberpunk cityscape with neon",
    "Magical forest with fireflies",
    "Abstract music visualization",
  ];

  return (
    <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-73px)]">
      {/* Left Panel - Controls */}
      <div className="lg:w-96 flex-shrink-0 p-4 border-b lg:border-b-0 lg:border-r border-white/10 lg:overflow-y-auto">
        <div className="mb-4">
          <motion.button
            onClick={() => setIsHistoryOpen(true)}
            disabled={history.length === 0}
            className="w-full flex items-center justify-center gap-2 p-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg hover:bg-white/10 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: history.length > 0 ? 1.02 : 1 }}
            whileTap={{ scale: history.length > 0 ? 0.98 : 1 }}
          >
            <History size={16} className="text-purple-400" />
            <span className="text-sm font-medium text-white/90">
              History ({history.length})
            </span>
          </motion.button>
        </div>

        {/* Prompt Input */}
        <div className="mb-6">
          <div
            className={`relative bg-white/5 backdrop-blur-xl border-2 ${
              isFocused ? "border-purple-500/50" : "border-white/10"
            } rounded-xl transition-all duration-300 overflow-hidden`}
          >
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={handlePromptChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Describe what you want to create..."
              className="w-full bg-transparent p-4 text-sm placeholder-white/40 text-white focus:outline-none resize-none overflow-hidden leading-relaxed"
              rows={3}
            />
            <motion.button
              onClick={handleGenerate}
              disabled={!prompt || isGenerating || !hasSufficientFlux}
              className="absolute bottom-2 right-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              whileHover={{
                scale: prompt && !isGenerating && hasSufficientFlux ? 1.05 : 1,
              }}
              whileTap={{
                scale: prompt && !isGenerating && hasSufficientFlux ? 0.95 : 1,
              }}
            >
              {isGenerating ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Send size={16} />
                  <div className="flex items-center gap-1 text-xs">
                    <Zap size={12} />
                    <span>{numberOfImages * 5}</span>
                  </div>
                </>
              )}
            </motion.button>
          </div>
        </div>

        {/* Style Selection */}
        <div className="mb-6">
          <motion.button
            onClick={() => setStylesOpen(!stylesOpen)}
            className="flex items-center justify-between w-full"
          >
            <h3 className="text-sm font-medium text-white/90 flex items-center gap-2">
              <Palette size={16} />
              Style
            </h3>
            <motion.div
              animate={{ rotate: stylesOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown size={16} className="text-white/60" />
            </motion.div>
          </motion.button>
          <AnimatePresence>
            {stylesOpen && (
              <motion.div
                initial={false}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div
                  className="mt-3 flex overflow-x-auto gap-2 pb-2"
                  style={{ scrollbarWidth: "none" }}
                >
                  <div className="grid grid-rows-2 grid-flow-col gap-2">
                    {styles.map((style) => {
                      const Icon = style.icon;
                      return (
                        <motion.button
                          key={style.name}
                          onClick={() => setSelectedStyle(style.value)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`relative p-3 rounded-lg transition-all duration-300 ${
                            selectedStyle === style.value
                              ? "bg-white/10 border-2 border-purple-500/50"
                              : "bg-white/5 border-2 border-white/10 hover:bg-white/10"
                          }`}
                        >
                          <div
                            className={`w-6 h-6 rounded-md bg-gradient-to-r ${style.gradient} flex items-center justify-center mb-1 mx-auto`}
                          >
                            <Icon size={12} className="text-white" />
                          </div>
                          <p className="text-xs font-medium text-white/80">
                            {style.name}
                          </p>
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Quick Prompts */}
        <div className="mb-6">
          <motion.button
            onClick={() => setQuickIdeasOpen(!quickIdeasOpen)}
            className="flex items-center justify-between w-full"
          >
            <h3 className="text-sm font-medium text-white/90 flex items-center gap-2">
              <Zap size={16} />
              Quick Ideas
            </h3>
            <motion.div
              animate={{ rotate: quickIdeasOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown size={16} className="text-white/60" />
            </motion.div>
          </motion.button>
          <AnimatePresence>
            {quickIdeasOpen && (
              <motion.div
                initial={false}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div
                  className="mt-3 flex overflow-x-auto gap-2 pb-2"
                  style={{ scrollbarWidth: "none" }}
                >
                  <div className="grid grid-rows-2 grid-flow-col gap-2">
                    {examplePrompts.map((examplePrompt, index) => (
                      <motion.button
                        key={index}
                        onClick={() => setPrompt(examplePrompt)}
                        whileHover={{ scale: 1.01 }}
                        className="w-40 text-left p-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg hover:bg-white/10 transition-all duration-300 group"
                      >
                        <p className="text-xs text-white/70 group-hover:text-white transition-colors">
                          {examplePrompt}
                        </p>
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Advanced Settings */}
        <div>
          <motion.button
            onClick={() => setAdvancedSettingsOpen(!advancedSettingsOpen)}
            className="flex items-center justify-between w-full p-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg hover:bg-white/10 transition-all duration-300"
          >
            <div className="flex items-center gap-2">
              <Settings size={16} className="text-purple-400" />
              <span className="text-sm font-medium text-white/90">
                Advanced
              </span>
            </div>
            <motion.div
              animate={{ rotate: advancedSettingsOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown size={16} className="text-white/60" />
            </motion.div>
          </motion.button>

          <AnimatePresence>
            {advancedSettingsOpen && (
              <motion.div
                initial={false}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-4 mt-2 space-y-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg">
                  <div>
                    <h4 className="text-xs font-medium text-white/80 mb-2">
                      Number of Images
                    </h4>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min="1"
                        max="4"
                        value={numberOfImages}
                        onChange={(e) =>
                          setNumberOfImages(parseInt(e.target.value))
                        }
                        className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-sm font-bold text-white">
                        {numberOfImages}
                      </span>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-white/80 mb-2">
                      Aspect Ratio
                    </h4>
                    <div className="grid grid-cols-4 gap-1">
                      {["1:1", "16:9", "9:16", "4:3", "3:4"].map((ratio) => (
                        <button
                          key={ratio}
                          onClick={() => setAspectRatio(ratio)}
                          className={`py-1 px-2 rounded text-xs transition-all duration-300 ${
                            aspectRatio === ratio
                              ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white"
                              : "bg-white/10 hover:bg-white/20 text-white/70"
                          }`}
                        >
                          {ratio}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xs font-medium text-white/80 mb-2">
                      Quality
                    </h4>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/70">Standard</span>
                      <button
                        onClick={() =>
                          setQuality(
                            quality === "standard" ? "high" : "standard"
                          )
                        }
                        className={`relative inline-flex items-center h-5 rounded-full w-9 transition-all duration-300 ${
                          quality === "high"
                            ? "bg-gradient-to-r from-purple-600 to-blue-600"
                            : "bg-white/20"
                        }`}
                      >
                        <motion.span
                          animate={{ x: quality === "high" ? 16 : 2 }}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 20,
                          }}
                          className="inline-block w-3 h-3 bg-white rounded-full shadow-lg"
                        />
                      </button>
                      <span className="text-xs text-white/70">High</span>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xs font-medium text-white/80 mb-2">
                      Seed
                    </h4>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={seed}
                        onChange={(e) => setSeed(e.target.value)}
                        placeholder="Random"
                        className="flex-1 bg-white/10 border border-white/20 p-2 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500/50 text-white placeholder-white/40 transition-all"
                      />
                      <motion.button
                        onClick={() =>
                          setSeed(String(Math.floor(Math.random() * 100000)))
                        }
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="p-2 bg-white/10 border border-white/20 rounded hover:bg-white/20 transition-all"
                      >
                        <RefreshCw size={12} className="text-white/80" />
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Right Panel - Results */}
      <div className="flex-1 p-4 lg:overflow-y-auto" ref={resultsRef}>
        <AnimatePresence mode="wait">
          {isGenerating ? (
            <div className="flex flex-col items-center justify-center h-full">
              <LoadingIndicator key="loading" size="w-16 h-16" />
              <motion.div
                initial={{ opacity: 0.5 }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
              >
                <p className="text-lg font-semibold text-white/90 mb-2">
                  Creating...
                </p>
                <p className="text-white/60 text-sm">This may take a moment</p>
              </motion.div>
            </div>
          ) : generatedImages.length > 0 ? (
            <ResultsGrid
              key="results"
              images={generatedImages}
              onPublish={handlePublish}
              onContinueEditing={handleContinueEditing}
            />
          ) : (
            <EmptyState key="empty" />
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {isHistoryOpen && (
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-end"
            onClick={() => setIsHistoryOpen(false)}
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="w-full max-w-md bg-gray-900/95 backdrop-blur-xl border-l border-white/10 p-6 overflow-y-auto pt-16 md:pt-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <History size={20} className="text-purple-400" />
                  Generation History
                </h2>
                <button
                  onClick={() => setIsHistoryOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X size={20} className="text-white/60" />
                </button>
              </div>

              <div className="space-y-3">
                {history.map((item) => (
                  <motion.div
                    key={item.id}
                    onClick={() => loadHistoryItem(item)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all duration-300 hover:bg-white/10 ${
                      currentHistoryId === item.id
                        ? "bg-purple-500/20 border-purple-500/50"
                        : "bg-white/5 border-white/10"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="grid grid-cols-2 gap-1 w-16 h-16 flex-shrink-0">
                        <img
                          src={
                            item.thumbnailUrl ||
                            "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
                          }
                          alt=""
                          className="w-full h-full object-cover rounded"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white/90 line-clamp-2 mb-1">
                          {item.prompt}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-white/60">
                          <span>{item.tags[0]}</span>
                          <span>•</span>
                          <span>{item.type}</span>
                        </div>
                        <p className="text-xs text-white/40 mt-1">
                          {new Date(
                            (item.createdAt as Timestamp).seconds * 1000
                          ).toLocaleDateString()}
                        </p>
                        {currentHistoryId === item.id && (
                          <div className="flex items-center gap-1 mt-2">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <span className="text-xs text-purple-400">
                              Active
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {history.length === 0 && (
                <div className="text-center py-12">
                  <History size={48} className="text-white/20 mx-auto mb-4" />
                  <p className="text-white/60">No generations yet</p>
                  <p className="text-white/40 text-sm mt-1">
                    Your generation history will appear here
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {publishModalOpen && selectedImageForPublish && (
        <PublishContent
          item={selectedImageForPublish}
          onClose={() => {
            setPublishModalOpen(false);
            setSelectedImageForPublish(null);
          }}
        />
      )}

      <InsufficientFluxModal
        isOpen={isFluxModalOpen}
        onClose={() => setIsFluxModalOpen(false)}
      />
    </div>
  );
};

// Empty State Component
const EmptyState: React.FC = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex items-center justify-center h-full"
  >
    <div className="text-center max-w-md">
      <div className="w-24 h-24 mx-auto mb-6 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-full animate-pulse"></div>
        <div className="absolute inset-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
          <Sparkles size={32} className="text-white" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-white mb-3">Ready to Create?</h2>
      <p className="text-white/60 mb-6 leading-relaxed">
        Describe your vision in the prompt box and watch AI bring your ideas to
        life.
      </p>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="p-3 bg-white/5 rounded-lg border border-white/10">
          <div className="w-6 h-6 bg-gradient-to-r from-emerald-500 to-teal-600 rounded mb-2 flex items-center justify-center">
            <Camera size={12} className="text-white" />
          </div>
          <p className="text-white/70">Photorealistic images</p>
        </div>
        <div className="p-3 bg-white/5 rounded-lg border border-white/10">
          <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-600 rounded mb-2 flex items-center justify-center">
            <Palette size={12} className="text-white" />
          </div>
          <p className="text-white/70">Artistic styles</p>
        </div>
        <div className="p-3 bg-white/5 rounded-lg border border-white/10">
          <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-600 rounded mb-2 flex items-center justify-center">
            <VideoIcon size={12} className="text-white" />
          </div>
          <p className="text-white/70">Cinematic looks</p>
        </div>
        <div className="p-3 bg-white/5 rounded-lg border border-white/10">
          <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-cyan-600 rounded mb-2 flex items-center justify-center">
            <Sparkles size={12} className="text-white" />
          </div>
          <p className="text-white/70">Creative concepts</p>
        </div>
      </div>
    </div>
  </motion.div>
);

interface ResultsGridProps {
  images: LibraryItem[];
  onPublish: (image: LibraryItem) => void;
  onContinueEditing: (image: LibraryItem) => void;
}

const ResultsGrid: React.FC<ResultsGridProps> = ({
  images,
  onPublish,
  onContinueEditing,
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1 },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white/90 flex items-center gap-2">
          <Sparkles size={20} className="text-purple-400" />
          Your Creations
        </h2>
        <span className="text-sm text-white/60">{images.length} images</span>
      </div>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4"
      >
        {images.map((image) => (
          <motion.div key={image.id} variants={itemVariants}>
            <CompactImageCard
              image={image}
              onPublish={onPublish}
              onContinueEditing={onContinueEditing}
            />
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

interface ImageCardProps {
  image: LibraryItem;
  onPublish: (image: LibraryItem) => void;
  onContinueEditing: (image: LibraryItem) => void;
}

const CompactImageCard: React.FC<ImageCardProps> = ({
  image,
  onPublish,
  onContinueEditing,
}) => {
  const [isLiked, setIsLiked] = useState(false);

  // Dummy handlers
  const handleLike = () => setIsLiked(!isLiked);
  const handleDownload = () => console.log("Download:", image.id);
  const handleRegenerate = () => console.log("Regenerate:", image.id);

  return (
    <motion.div
      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden group"
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
    >
      <div className="relative">
        <img
          src={
            image.fullUrl ||
            "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
          }
          alt={image.prompt}
          className="w-full aspect-square object-cover"
        />
      </div>

      {/* Action buttons in horizontal row at bottom */}
      <div className="p-3">
        <div className="flex items-center justify-center gap-2">
          <motion.button
            onClick={() => onContinueEditing(image)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex-1 p-3 rounded-lg backdrop-blur-sm bg-white/10 hover:bg-white/20 text-white/80 border border-white/20 transition-all text-sm font-medium"
          >
            <Wand2 size={16} className="mx-auto" />
          </motion.button>

          <motion.button
            onClick={handleDownload}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex-1 p-3 rounded-lg backdrop-blur-sm bg-white/10 hover:bg-white/20 text-white/80 border border-white/20 transition-all text-sm font-medium"
          >
            <Download size={16} className="mx-auto" />
          </motion.button>

          <motion.button
            onClick={() => onPublish(image)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex-1 p-3 rounded-lg backdrop-blur-sm bg-purple-600/40 hover:bg-purple-600/60 text-purple-300 border border-purple-500/50 transition-all text-sm font-medium"
          >
            <Share size={16} className="mx-auto" />
          </motion.button>

          <motion.button
            onClick={handleRegenerate}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex-1 p-3 rounded-lg backdrop-blur-sm bg-white/10 hover:bg-white/20 text-white/80 border border-white/20 transition-all text-sm font-medium"
          >
            <RefreshCw size={16} className="mx-auto" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default ImageGenerator;
